/**
 * ترتيب عناصر التنقل وإظهارها/إخفاؤها — تفضيل جهاز لكل مستخدم.
 * لا يغيّر الصلاحيات؛ العنصر المخفي يبقى متاحًا عبر رابطه المباشر.
 */

import { useSyncExternalStore } from 'react';
import { getDeviceSetting, setDeviceSetting } from '@/services/settings-service';

export interface NavPrefs {
  order: string[];
  hidden: string[];
}

export interface NavCatalogItem {
  to: string;
  label: string;
  group: string;
}

export const NAV_CATALOG: NavCatalogItem[] = [
  { to: '/', label: 'لوحة المهام', group: 'العمل' },
  { to: '/tasks/assigned', label: 'المسندة إليّ', group: 'العمل' },
  { to: '/tasks/created', label: 'أنشأتها', group: 'العمل' },
  { to: '/tasks/supervised', label: 'تحت إشرافي', group: 'العمل' },
  { to: '/tasks/personal', label: 'مهامي الشخصية', group: 'العمل' },
  { to: '/tasks/overdue', label: 'المتأخرة', group: 'العمل' },
  { to: '/templates', label: 'قوالب التكرار', group: 'المتابعة' },
  { to: '/reports', label: 'التقارير', group: 'المتابعة' },
  { to: '/admin/company', label: 'إعدادات المنشأة', group: 'إعدادات المنشأة' },
  { to: '/admin/branches', label: 'الفروع', group: 'إعدادات المنشأة' },
  { to: '/admin/brands', label: 'العلامات التجارية', group: 'إعدادات المنشأة' },
  { to: '/admin/users', label: 'المستخدمون والعضويات', group: 'إعدادات المنشأة' },
  { to: '/admin/roles', label: 'الأدوار والصلاحيات', group: 'إعدادات المنشأة' },
  { to: '/admin/supervision', label: 'الإشراف', group: 'إعدادات المنشأة' },
  { to: '/admin/categories', label: 'فئات المهام', group: 'إعدادات المنشأة' },
  { to: '/admin/theming', label: 'المظهر والهوية', group: 'إعدادات المنشأة' },
  { to: '/admin/retention', label: 'الملفات والاحتفاظ', group: 'إعدادات المنشأة' },
  { to: '/admin/audit', label: 'سجل التدقيق', group: 'إعدادات المنشأة' },
  { to: '/notifications', label: 'الإشعارات', group: 'عام' },
  { to: '/profile', label: 'الملف الشخصي', group: 'عام' },
  { to: '/settings', label: 'الإعدادات الشخصية', group: 'عام' },
];

const STORAGE_KEY = 'nav.prefs';
const EMPTY: NavPrefs = { order: [], hidden: [] };

let current: NavPrefs | null = null;
const listeners = new Set<() => void>();

function sanitize(value: unknown): NavPrefs {
  if (!value || typeof value !== 'object') return EMPTY;
  const raw = value as Partial<NavPrefs>;
  const known = new Set(NAV_CATALOG.map((item) => item.to));
  const order = Array.isArray(raw.order)
    ? raw.order.filter((to): to is string => typeof to === 'string' && known.has(to))
    : [];
  const hidden = Array.isArray(raw.hidden)
    ? raw.hidden.filter((to): to is string => typeof to === 'string' && known.has(to))
    : [];
  return { order: [...new Set(order)], hidden: [...new Set(hidden)] };
}

export function getNavPrefs(): NavPrefs {
  if (!current) current = sanitize(getDeviceSetting<unknown>(STORAGE_KEY, EMPTY));
  return current;
}

export function saveNavPrefs(next: Partial<NavPrefs>): NavPrefs {
  current = sanitize({ ...getNavPrefs(), ...next });
  setDeviceSetting(STORAGE_KEY, current);
  for (const listener of listeners) listener();
  return current;
}

export function resetNavPrefs(): NavPrefs {
  current = EMPTY;
  setDeviceSetting(STORAGE_KEY, EMPTY);
  for (const listener of listeners) listener();
  return current;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useNavPrefs(): NavPrefs {
  return useSyncExternalStore(subscribe, getNavPrefs, getNavPrefs);
}

/** يرتّب عناصر التنقل حسب التفضيل ويستبعد المخفي. غير المعروف يبقى في النهاية. */
export function applyNavPrefs<T extends { to: string }>(items: T[], prefs: NavPrefs): T[] {
  const position = new Map(prefs.order.map((to, index) => [to, index]));
  return items
    .filter((item) => !prefs.hidden.includes(item.to))
    .sort((a, b) => {
      const left = position.get(a.to) ?? Number.MAX_SAFE_INTEGER;
      const right = position.get(b.to) ?? Number.MAX_SAFE_INTEGER;
      return left - right;
    });
}

/** الترتيب الحالي لعناصر الكتالوج (ما لم يُرتَّب يبقى ترتيب الكتالوج). */
export function orderedCatalog(prefs: NavPrefs): NavCatalogItem[] {
  return applyNavPrefs(NAV_CATALOG, prefs);
}

/**
 * كل عناصر الكتالوج مرتبة، بما فيها المخفية.
 * تستخدمها شاشة الإعدادات كي يبقى العنصر المخفي ظاهرًا ويمكن إعادة تفعيله.
 */
export function allNavItems(prefs: NavPrefs): NavCatalogItem[] {
  const position = new Map(prefs.order.map((to, index) => [to, index]));
  return [...NAV_CATALOG].sort((a, b) => {
    const left = position.get(a.to) ?? Number.MAX_SAFE_INTEGER;
    const right = position.get(b.to) ?? Number.MAX_SAFE_INTEGER;
    return left - right;
  });
}

export function moveNavItem(prefs: NavPrefs, to: string, direction: -1 | 1): NavPrefs {
  const order = orderedCatalog(prefs).map((item) => item.to);
  const index = order.indexOf(to);
  const target = index + direction;
  if (index < 0 || target < 0 || target >= order.length) return prefs;
  [order[index], order[target]] = [order[target]!, order[index]!];
  return saveNavPrefs({ order });
}

/**
 * ينقل عنصرًا إلى موضع عنصر آخر بالسحب والإفلات.
 * يُسمح بالترتيب داخل المجموعة نفسها فقط لأن الشريط الجانبي يعرض المجموعات
 * بترتيب ثابت، ونقل العنصر بين مجموعتين لن يغيّر مكانه المرئي.
 */
export function moveNavItemTo(prefs: NavPrefs, from: string, to: string): NavPrefs {
  if (from === to) return prefs;
  const items = allNavItems(prefs);
  const fromIndex = items.findIndex((item) => item.to === from);
  const toIndex = items.findIndex((item) => item.to === to);
  if (fromIndex < 0 || toIndex < 0) return prefs;
  if (items[fromIndex]!.group !== items[toIndex]!.group) return prefs;
  const order = items.map((item) => item.to);
  const [moved] = order.splice(fromIndex, 1);
  order.splice(toIndex, 0, moved!);
  return saveNavPrefs({ order });
}

/** يظهر/يخفي عنصرًا دون التأثير على ترتيب بقية العناصر. */
export function setNavItemHidden(prefs: NavPrefs, to: string, hidden: boolean): NavPrefs {
  const next = hidden
    ? [...prefs.hidden.filter((entry) => entry !== to), to]
    : prefs.hidden.filter((entry) => entry !== to);
  return saveNavPrefs({ hidden: next });
}
